import React from 'react';
import MetricCard from '../twin/MetricCard';
import JourneyTrack from '../twin/JourneyTrack';

// A full-width "Dial": one metric of the digital twin. The real trajectory
// chart — measured history with all its ups and downs, then the dotted
// projection — is the primary visual (progress is never a straight line).
// Metrics without enough history to chart yet fall back to the compact
// start→now→target track until the twin has something real to draw.

// Render coaching copy the way the artifact did: calm warm-gray body with the
// numbers picked out bright + bold, so the eye lands on the quantities.
const emphasize = (text) =>
  String(text)
    .split(/(\d[\d,.:%]*)/g)
    .map((part, i) =>
      /^\d/.test(part)
        ? <strong key={i} className="font-semibold text-gray-900 dark:text-white">{part}</strong>
        : part
    );

const DialCard = ({ card, metric, onUnpin }) => {
  const hasChart = metric && metric.enough_data;
  const noData = card.now == null;

  return (
    <div className="card-glow rounded-2xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card p-5 transition-shadow hover:shadow-card">
      <div className="flex items-start justify-between gap-2 mb-3">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white min-w-0 truncate">
          {card.label}
        </h3>
        <div className="flex items-center gap-1.5 shrink-0">
          <span
            className={`text-sm px-2 py-0.5 rounded-full ${
              card.in_band
                ? 'bg-good/15 text-good-strong dark:text-good'
                : 'bg-gray-100 text-gray-500 dark:bg-surface-dark-raised dark:text-gray-400'
            }`}
          >
            {card.in_band ? 'in range' : 'on the way'}
          </span>
          <button
            onClick={() => onUnpin(card.metric_key)}
            aria-label={`Remove ${card.label} from home`}
            title="Remove from home"
            className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 bg-transparent border-none transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {hasChart ? (
        /* The digital twin proper: the real trajectory chart + this week's
           prediction + the Now/Next/12-week/Target readouts. */
        <MetricCard metric={metric} bare />
      ) : (
        /* Not enough history to chart yet — show the compact start→now→target
           track so the metric still reads, and say the chart is coming. */
        <>
          <JourneyTrack
            start={card.start_value}
            now={card.now}
            low={card.ultimate_low}
            high={card.ultimate_high}
            next={card.next_value}
            nextWeeks={card.next_horizon_weeks}
            direction={card.direction}
            unit={card.unit}
          />
          {!noData && (
            <p className="mt-4 text-sm text-gray-400 dark:text-gray-500">
              Gathering data — the trajectory chart appears once there's enough history.
            </p>
          )}
        </>
      )}

      {/* The personalized AI "how to move this" tip — the coaching half the
          chart's math can't give. Streams in a moment after load. */}
      {card.suggestion ? (
        <p className="mt-3 text-sm leading-relaxed text-stone-600 dark:text-stone-300">
          {emphasize(card.suggestion)}
        </p>
      ) : card.suggestion_stale ? (
        <p className="mt-3 text-sm text-gray-400 dark:text-gray-500">
          Personalizing your next step…
        </p>
      ) : null}
    </div>
  );
};

export default DialCard;
